import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import {
  ArrowRight,
  CalendarClock,
  GitCompareArrows,
  History,
  Filter,
  Cloud,
  ScrollText,
  Plug,
  Settings as SettingsIcon,
  Rocket,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

function Hero() {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-0 grid-bg opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background" />
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 sm:pb-32 sm:pt-28 lg:px-8 lg:pb-40 lg:pt-36">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 border border-border bg-card px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              <span className="inline-block h-1.5 w-1.5 bg-[#002FA7]" />
              {t("landing.tagline")}
            </div>
            <h1
              className="fade-up mt-6 font-display text-5xl font-black leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl"
              data-testid="hero-title"
            >
              {t("landing.heroTitle")}
            </h1>
            <p className="fade-up mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl" style={{ animationDelay: "80ms" }}>
              {t("landing.heroSubtitle")}
            </p>
            <div className="fade-up mt-10 flex flex-wrap items-center gap-3" style={{ animationDelay: "160ms" }}>
              <Button
                asChild
                size="lg"
                className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white px-6 h-12"
                data-testid="hero-cta-primary"
              >
                <Link to={user ? "/dashboard" : "/login"}>
                  {t("landing.ctaPrimary")} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                asChild
                className="rounded-sm border-foreground/20 px-6 h-12"
                data-testid="hero-cta-secondary"
              >
                <a href="#features">{t("landing.ctaSecondary")}</a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="relative border border-border bg-card p-5 fade-up" style={{ animationDelay: "220ms" }}>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="font-mono text-[11px] uppercase text-muted-foreground">job/contracts-monthly</span>
                <span className="flex items-center gap-1 font-mono text-[11px] text-emerald-600 dark:text-emerald-400">
                  <span className="h-1.5 w-1.5 bg-emerald-600 dark:bg-emerald-400" /> RUNNING
                </span>
              </div>
              <div className="mt-4 space-y-1.5 font-mono text-xs">
                <Line ok>copied Documents/Invoices/inv-2024-001.pdf</Line>
                <Line ok>copied Photos/2024/holidays/IMG_1042.jpg</Line>
                <Line muted>skipped Archive 2024/old-contracts.zip — up-to-date</Line>
                <Line ok>copied Projects/savebox/spec.md</Line>
                <Line ok>copied Projects/savebox/architecture.png</Line>
                <Line>checking Documents/report-q3.pdf…</Line>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 border-t border-border pt-4 text-center">
                <Stat label="copied" value="248" />
                <Stat label="skipped" value="36" />
                <Stat label="MB" value="412" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Line({ children, ok, muted }) {
  return (
    <div className="flex items-start gap-2 leading-relaxed">
      <span className={`mt-1.5 inline-block h-1 w-1 ${ok ? "bg-emerald-500" : muted ? "bg-muted-foreground/40" : "bg-amber-500"}`} />
      <span className={muted ? "text-muted-foreground" : ""}>{children}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-xl font-black">{value}</div>
      <div className="font-mono text-[10px] uppercase text-muted-foreground">{label}</div>
    </div>
  );
}

function Features() {
  const { t } = useI18n();
  const items = [
    { icon: CalendarClock, k: "f1" },
    { icon: GitCompareArrows, k: "f2" },
    { icon: History, k: "f3" },
    { icon: Filter, k: "f4" },
    { icon: Cloud, k: "f5" },
    { icon: ScrollText, k: "f6" },
  ];
  return (
    <section id="features" className="border-b border-border bg-background py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// 01 — Features</div>
          <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-5xl">
            {t("landing.featuresTitle")}
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">{t("landing.featuresSubtitle")}</p>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-px border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
          {items.map(({ icon: Icon, k }) => (
            <div key={k} className="group bg-background p-8 transition hover:bg-accent/40">
              <div className="flex h-10 w-10 items-center justify-center border border-border bg-card transition group-hover:border-[#002FA7] group-hover:text-[#002FA7]">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{t(`landing.${k}Title`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`landing.${k}Desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useI18n();
  const steps = [
    { icon: Plug, k: "step1" },
    { icon: SettingsIcon, k: "step2" },
    { icon: Rocket, k: "step3" },
  ];
  return (
    <section id="how" className="border-b border-border bg-background py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// 02 — Workflow</div>
        <h2 className="mt-3 max-w-3xl font-display text-3xl font-black tracking-tight sm:text-5xl">
          {t("landing.howTitle")}
        </h2>
        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {steps.map(({ icon: Icon, k }, i) => (
            <div key={k} className="relative border border-border bg-card p-8">
              <div className="absolute right-4 top-4 font-mono text-xs text-muted-foreground">0{i + 1}</div>
              <div className="flex h-12 w-12 items-center justify-center border border-foreground bg-foreground text-background">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{t(`landing.${k}`)}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`landing.${k}Desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const { t } = useI18n();
  const items = ["faq1", "faq2", "faq3", "faq4"];
  return (
    <section id="faq" className="border-b border-border bg-background py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// 03 — FAQ</div>
        <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-5xl">{t("landing.faqTitle")}</h2>
        <Accordion type="single" collapsible className="mt-10">
          {items.map((k) => (
            <AccordionItem key={k} value={k} className="border-border">
              <AccordionTrigger className="text-left font-display text-base font-bold hover:no-underline" data-testid={`faq-${k}-trigger`}>
                {t(`landing.${k}Q`)}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {t(`landing.${k}A`)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden border-b border-border bg-foreground text-background">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="font-display text-4xl font-black tracking-tight sm:text-5xl">
            {t("landing.ctaBottomTitle")}
          </h2>
          <p className="mt-4 text-lg text-background/70">{t("landing.ctaBottomSubtitle")}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white px-6 h-12"
              data-testid="bottom-cta-button"
            >
              <Link to={user ? "/dashboard" : "/login"}>
                {t("landing.ctaPrimary")} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-2 text-sm text-background/80">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <span className="font-mono">No PC required · cloud-to-cloud</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="App">
      <Header variant="marketing" />
      <main>
        <Hero />
        <Features />
        <HowItWorks />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
